/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { uccFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = uccFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_COURSE_CATEGORY: '/client/api/v1/course-category',
  CLIENT_V1_USERS_USERID_INTRODUCE: (userId: string) => `/client/api/v1/users/${userId}/introduce`,
  CLIENT_V1_USERS_ID: '/client/api/v1/users/id',
  CLIENT_V1_USERS_USERID_IS_FOLLOWING: (userId: string) =>
    `/client/api/v1/users/${userId}/is-following`,
  CLIENT_V1_USERS_ID_SLUG: (id: string) => `/client/api/v1/users/${id}/slug`,
  CLIENT_V1_USERS_USERID_PROFILE: (userId: string) => `/client/api/v1/users/${userId}/profile`,
  CLIENT_V1_COURSE_USER_USERID: (userId: string) => `/client/api/v1/course/user/${userId}`,
  CLIENT_V1_COURSES_COURSEID_LIKE: (courseId: string) => `/client/api/v1/courses/${courseId}/like`,
  CLIENT_V2_COURSES_ID_MY_VOUCHERS: (id: string) => `/client/api/v2/courses/${id}/my-vouchers`,
  CLIENT_V1_POSTS_USER_USERID: (userId: string) => `/client/api/v1/posts/user/${userId}`,
  CLIENT_V1_BOOKMARKS_QUESTION_ID: (id: string) => `/client/api/v1/bookmarks/question/${id}`,
  CLIENT_V1_FILES_POST_IMAGE: '/client/api/v1/files/post-image',
  CLIENT_V1_NOTES_ID: (id: string) => `/client/api/v1/notes/${id}`,
  CLIENT_V1_NOTES_PUBLISH: '/client/api/v1/notes/publish',
  CLIENT_V1_NOTES_UNPUBLISH: '/client/api/v1/notes/unpublish',
  CLIENT_V1_NOTES_ME: '/client/api/v1/notes/me',
  CLIENT_V1_NOTES_MY_RECENTS: '/client/api/v1/notes/my-recents',
  CLIENT_V1_NOTES_ID_WITH_UNITS: (id: string) => `/client/api/v1/notes/${id}/with-units`,
  CLIENT_V1_NOTES_ID_METADATA: (id: string) => `/client/api/v1/notes/${id}/metadata`,
  CLIENT_V1_GUIDES_ID: (id: string) => `/client/api/v1/guides/${id}`,
  CLIENT_V1_QUESTIONS_ME: '/client/api/v1/questions/me',
  CLIENT_V1_QUESTIONS_BOOKMARKS: '/client/api/v1/questions/bookmarks',
  CLIENT_V1_STUDIES_ME: '/client/api/v1/studies/me',
  CLIENT_V1_CHATS_ME: '/client/api/v1/chats/me',
  CLIENT_V1_BLOGS_USER_USERID: (userId: string) => `/client/api/v1/blogs/user/${userId}`,
  CLIENT_V1_BLOGS_USER_USERID_CATEGORY: (userId: string) =>
    `/client/api/v1/blogs/user/${userId}/category`,
  CLIENT_V1_BLOGS_USER_USERID_TAG: (userId: string) => `/client/api/v1/blogs/user/${userId}/tag`,
  CLIENT_V1_COMMUNITIES_FEEDS: '/client/api/v1/communities/feeds',
  CLIENT_V1_PROJECTS_ME: '/client/api/v1/projects/me',
  CLIENT_V1_REVIEWS_COURSE_USER_USERID_RECEIVED: (userId: string) =>
    `/client/api/v1/reviews/course/user/${userId}/received`,
  CLIENT_V1_REVIEWS_COURSES_COURSEID_BEST_REVIEWS: (courseId: string) =>
    `/client/api/v1/reviews/courses/${courseId}/best-reviews`,
  CLIENT_V1_REVIEWS_ID_CONTENT: (id: string) => `/client/api/v1/reviews/${id}/content`,
  CLIENT_V1_FOLLOWS_USER_ID: (id: string) => `/client/api/v1/follows/user/${id}`,
  CLIENT_V1_INSTRUCTORS_ID_PROFILE: (id: string) => `/client/api/v1/instructors/${id}/profile`,
  CLIENT_V1_INSTRUCTORS_USERID_CLIPS: (userId: string) =>
    `/client/api/v1/instructors/${userId}/clips`,
  CLIENT_V1_FAQS: '/client/api/v1/faqs',
  CLIENT_V1_USER_DEVICES: '/client/api/v1/user-devices',
  CLIENT_V1_USER_DEVICES_REGISTER: '/client/api/v1/user-devices/register',
  CLIENT_V1_USER_DEVICES_SEND_VERIFICATION_EMAIL:
    '/client/api/v1/user-devices/send-verification-email',
  CLIENT_V1_USER_DEVICES_EMAIL_VERIFICATION: '/client/api/v1/user-devices/email-verification',
  CLIENT_V1_USER_DEVICES_CONFIRM_TOKEN: '/client/api/v1/user-devices/confirm-token',
  CLIENT_V1_USER_DEVICES_ID_REMOVE: (id: string) => `/client/api/v1/user-devices/${id}/remove`,
  CLIENT_V1_CLIPS_USERS_HISTORIES: '/client/api/v1/clips/users/histories',
  CLIENT_V1_CLIPS: '/client/api/v1/clips',
  CLIENT_V1_HALLOWEEN_CHALLENGES_DRAW_STATUS: '/client/api/v1/halloween-challenges/draw-status',
  CLIENT_V1_HALLOWEEN_CHALLENGES_DRAW_POINTS: '/client/api/v1/halloween-challenges/draw-points',
  CLIENT_V1_RANDOM_POINTS_DRAW_STATUS: '/client/api/v1/random-points/draw-status',
  CLIENT_V1_RANDOM_POINTS_DRAW_COURSE: '/client/api/v1/random-points/draw/course',
  CLIENT_V1_RANDOM_POINTS_DRAW: '/client/api/v1/random-points/draw',
  CLIENT_V1_USER_ONBOARDINGS_ANSWERS: '/client/api/v1/user-onboardings/answers',
} as const;
export function useClientApiV1UsersUserIdIntroduceUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UsersUserIdIntroduceUpdateResponse,
    unknown,
    ClientApiV1UsersUserIdIntroduceUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UsersUserIdIntroduceUpdateResponse,
    unknown,
    ClientApiV1UsersUserIdIntroduceUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UsersUserIdIntroduceUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1CoursesCourseIdLikeUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CoursesCourseIdLikeUpdateResponse,
    unknown,
    ClientApiV1CoursesCourseIdLikeUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CoursesCourseIdLikeUpdateResponse,
    unknown,
    ClientApiV1CoursesCourseIdLikeUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CoursesCourseIdLikeUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1BookmarksQuestionIdUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1BookmarksQuestionIdUpdateResponse,
    unknown,
    ClientApiV1BookmarksQuestionIdUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1BookmarksQuestionIdUpdateResponse,
    unknown,
    ClientApiV1BookmarksQuestionIdUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1BookmarksQuestionIdUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1FilesPostImageCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1FilesPostImageCreateResponse,
    unknown,
    ClientApiV1FilesPostImageCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1FilesPostImageCreateResponse,
    unknown,
    ClientApiV1FilesPostImageCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1FilesPostImageCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1NotesIdUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1NotesIdUpdateResponse,
    unknown,
    ClientApiV1NotesIdUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1NotesIdUpdateResponse,
    unknown,
    ClientApiV1NotesIdUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1NotesIdUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1NotesIdDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1NotesIdDeleteResponse,
    unknown,
    ClientApiV1NotesIdDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1NotesIdDeleteResponse,
    unknown,
    ClientApiV1NotesIdDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1NotesIdDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1NotesPublishCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1NotesPublishCreateResponse,
    unknown,
    ClientApiV1NotesPublishCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1NotesPublishCreateResponse,
    unknown,
    ClientApiV1NotesPublishCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1NotesPublishCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1NotesUnpublishCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1NotesUnpublishCreateResponse,
    unknown,
    ClientApiV1NotesUnpublishCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1NotesUnpublishCreateResponse,
    unknown,
    ClientApiV1NotesUnpublishCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1NotesUnpublishCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1GuidesIdDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1GuidesIdDeleteResponse,
    unknown,
    ClientApiV1GuidesIdDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1GuidesIdDeleteResponse,
    unknown,
    ClientApiV1GuidesIdDeleteVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1GuidesIdDeleteVariables) =>
      fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1FollowsUserIdUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1FollowsUserIdUpdateResponse,
    unknown,
    ClientApiV1FollowsUserIdUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1FollowsUserIdUpdateResponse,
    unknown,
    ClientApiV1FollowsUserIdUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1FollowsUserIdUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1UserDevicesRegisterCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UserDevicesRegisterCreateResponse,
    unknown,
    ClientApiV1UserDevicesRegisterCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UserDevicesRegisterCreateResponse,
    unknown,
    ClientApiV1UserDevicesRegisterCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UserDevicesRegisterCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1UserDevicesSendVerificationEmailCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UserDevicesSendVerificationEmailCreateResponse,
    unknown,
    ClientApiV1UserDevicesSendVerificationEmailCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UserDevicesSendVerificationEmailCreateResponse,
    unknown,
    ClientApiV1UserDevicesSendVerificationEmailCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UserDevicesSendVerificationEmailCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1UserDevicesEmailVerificationCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UserDevicesEmailVerificationCreateResponse,
    unknown,
    ClientApiV1UserDevicesEmailVerificationCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UserDevicesEmailVerificationCreateResponse,
    unknown,
    ClientApiV1UserDevicesEmailVerificationCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UserDevicesEmailVerificationCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1UserDevicesConfirmTokenCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UserDevicesConfirmTokenCreateResponse,
    unknown,
    ClientApiV1UserDevicesConfirmTokenCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UserDevicesConfirmTokenCreateResponse,
    unknown,
    ClientApiV1UserDevicesConfirmTokenCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UserDevicesConfirmTokenCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1UserDevicesIdRemoveCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UserDevicesIdRemoveCreateResponse,
    unknown,
    ClientApiV1UserDevicesIdRemoveCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UserDevicesIdRemoveCreateResponse,
    unknown,
    ClientApiV1UserDevicesIdRemoveCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UserDevicesIdRemoveCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1ClipsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1ClipsCreateResponse,
    unknown,
    ClientApiV1ClipsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1ClipsCreateResponse,
    unknown,
    ClientApiV1ClipsCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1ClipsCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1HalloweenChallengesDrawPointsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1HalloweenChallengesDrawPointsCreateResponse,
    unknown,
    ClientApiV1HalloweenChallengesDrawPointsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1HalloweenChallengesDrawPointsCreateResponse,
    unknown,
    ClientApiV1HalloweenChallengesDrawPointsCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1HalloweenChallengesDrawPointsCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1RandomPointsDrawCourseCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1RandomPointsDrawCourseCreateResponse,
    unknown,
    ClientApiV1RandomPointsDrawCourseCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1RandomPointsDrawCourseCreateResponse,
    unknown,
    ClientApiV1RandomPointsDrawCourseCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1RandomPointsDrawCourseCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1RandomPointsDrawCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1RandomPointsDrawCreateResponse,
    unknown,
    ClientApiV1RandomPointsDrawCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1RandomPointsDrawCreateResponse,
    unknown,
    ClientApiV1RandomPointsDrawCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1RandomPointsDrawCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1UserOnboardingsAnswersCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1UserOnboardingsAnswersCreateResponse,
    unknown,
    ClientApiV1UserOnboardingsAnswersCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1UserOnboardingsAnswersCreateResponse,
    unknown,
    ClientApiV1UserOnboardingsAnswersCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1UserOnboardingsAnswersCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1CourseCategory = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseCategoryResponse>(url, requestOptions);

export interface ClientApiV1CourseCategoryQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseCategoryResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseCategoryQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseCategoryQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseCategory(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseCategoryQuery(params: ClientApiV1CourseCategoryQueryParams) {
  const result = useQuery<ClientApiV1CourseCategoryResponse, unknown>(
    getClientApiV1CourseCategoryQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersUserIdIntroduce = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UsersUserIdIntroduceResponse>(url, requestOptions);

export interface ClientApiV1UsersUserIdIntroduceQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UsersUserIdIntroduceResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersUserIdIntroduceQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersUserIdIntroduceQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersUserIdIntroduce(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersUserIdIntroduceQuery(
  params: ClientApiV1UsersUserIdIntroduceQueryParams,
) {
  const result = useQuery<ClientApiV1UsersUserIdIntroduceResponse, unknown>(
    getClientApiV1UsersUserIdIntroduceQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1UsersIdResponse>(url, requestOptions);

export interface ClientApiV1UsersIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1UsersIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersId(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersIdQuery(params: ClientApiV1UsersIdQueryParams) {
  const result = useQuery<ClientApiV1UsersIdResponse, unknown>(
    getClientApiV1UsersIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersUserIdIsFollowing = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UsersUserIdIsFollowingResponse>(url, requestOptions);

export interface ClientApiV1UsersUserIdIsFollowingQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UsersUserIdIsFollowingResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersUserIdIsFollowingQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersUserIdIsFollowingQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersUserIdIsFollowing(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersUserIdIsFollowingQuery(
  params: ClientApiV1UsersUserIdIsFollowingQueryParams,
) {
  const result = useQuery<ClientApiV1UsersUserIdIsFollowingResponse, unknown>(
    getClientApiV1UsersUserIdIsFollowingQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersIdSlug = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1UsersIdSlugResponse>(url, requestOptions);

export interface ClientApiV1UsersIdSlugQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1UsersIdSlugResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersIdSlugQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersIdSlugQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersIdSlug(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersIdSlugQuery(params: ClientApiV1UsersIdSlugQueryParams) {
  const result = useQuery<ClientApiV1UsersIdSlugResponse, unknown>(
    getClientApiV1UsersIdSlugQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersUserIdProfile = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UsersUserIdProfileResponse>(url, requestOptions);

export interface ClientApiV1UsersUserIdProfileQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UsersUserIdProfileResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersUserIdProfileQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersUserIdProfileQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersUserIdProfile(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersUserIdProfileQuery(
  params: ClientApiV1UsersUserIdProfileQueryParams,
) {
  const result = useQuery<ClientApiV1UsersUserIdProfileResponse, unknown>(
    getClientApiV1UsersUserIdProfileQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1CourseUserUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CourseUserUserIdResponse>(url, requestOptions);

export interface ClientApiV1CourseUserUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CourseUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CourseUserUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CourseUserUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CourseUserUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1CourseUserUserIdQuery(
  params: ClientApiV1CourseUserUserIdQueryParams,
) {
  const result = useQuery<ClientApiV1CourseUserUserIdResponse, unknown>(
    getClientApiV1CourseUserUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2CoursesIdMyVouchers = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2CoursesIdMyVouchersResponse>(url, requestOptions);

export interface ClientApiV2CoursesIdMyVouchersQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2CoursesIdMyVouchersResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2CoursesIdMyVouchersQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2CoursesIdMyVouchersQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2CoursesIdMyVouchers(url, requestOptions),
  ...options,
});

export function useClientApiV2CoursesIdMyVouchersQuery(
  params: ClientApiV2CoursesIdMyVouchersQueryParams,
) {
  const result = useQuery<ClientApiV2CoursesIdMyVouchersResponse, unknown>(
    getClientApiV2CoursesIdMyVouchersQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1PostsUserUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1PostsUserUserIdResponse>(url, requestOptions);

export interface ClientApiV1PostsUserUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1PostsUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1PostsUserUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1PostsUserUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1PostsUserUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1PostsUserUserIdQuery(params: ClientApiV1PostsUserUserIdQueryParams) {
  const result = useQuery<ClientApiV1PostsUserUserIdResponse, unknown>(
    getClientApiV1PostsUserUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1NotesId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1NotesIdResponse>(url, requestOptions);

export interface ClientApiV1NotesIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1NotesIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1NotesIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1NotesIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1NotesId(url, requestOptions),
  ...options,
});

export function useClientApiV1NotesIdQuery(params: ClientApiV1NotesIdQueryParams) {
  const result = useQuery<ClientApiV1NotesIdResponse, unknown>(
    getClientApiV1NotesIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1NotesMe = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1NotesMeResponse>(url, requestOptions);

export interface ClientApiV1NotesMeQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1NotesMeResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1NotesMeQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1NotesMeQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1NotesMe(url, requestOptions),
  ...options,
});

export function useClientApiV1NotesMeQuery(params: ClientApiV1NotesMeQueryParams) {
  const result = useQuery<ClientApiV1NotesMeResponse, unknown>(
    getClientApiV1NotesMeQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1NotesMyRecents = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1NotesMyRecentsResponse>(url, requestOptions);

export interface ClientApiV1NotesMyRecentsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1NotesMyRecentsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1NotesMyRecentsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1NotesMyRecentsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1NotesMyRecents(url, requestOptions),
  ...options,
});

export function useClientApiV1NotesMyRecentsQuery(params: ClientApiV1NotesMyRecentsQueryParams) {
  const result = useQuery<ClientApiV1NotesMyRecentsResponse, unknown>(
    getClientApiV1NotesMyRecentsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1NotesIdWithUnits = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1NotesIdWithUnitsResponse>(url, requestOptions);

export interface ClientApiV1NotesIdWithUnitsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1NotesIdWithUnitsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1NotesIdWithUnitsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1NotesIdWithUnitsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1NotesIdWithUnits(url, requestOptions),
  ...options,
});

export function useClientApiV1NotesIdWithUnitsQuery(
  params: ClientApiV1NotesIdWithUnitsQueryParams,
) {
  const result = useQuery<ClientApiV1NotesIdWithUnitsResponse, unknown>(
    getClientApiV1NotesIdWithUnitsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1NotesIdMetadata = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1NotesIdMetadataResponse>(url, requestOptions);

export interface ClientApiV1NotesIdMetadataQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1NotesIdMetadataResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1NotesIdMetadataQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1NotesIdMetadataQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1NotesIdMetadata(url, requestOptions),
  ...options,
});

export function useClientApiV1NotesIdMetadataQuery(params: ClientApiV1NotesIdMetadataQueryParams) {
  const result = useQuery<ClientApiV1NotesIdMetadataResponse, unknown>(
    getClientApiV1NotesIdMetadataQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1GuidesId = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1GuidesIdResponse>(url, requestOptions);

export interface ClientApiV1GuidesIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1GuidesIdResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1GuidesIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1GuidesIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1GuidesId(url, requestOptions),
  ...options,
});

export function useClientApiV1GuidesIdQuery(params: ClientApiV1GuidesIdQueryParams) {
  const result = useQuery<ClientApiV1GuidesIdResponse, unknown>(
    getClientApiV1GuidesIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1QuestionsMe = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1QuestionsMeResponse>(url, requestOptions);

export interface ClientApiV1QuestionsMeQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1QuestionsMeResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1QuestionsMeQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1QuestionsMeQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1QuestionsMe(url, requestOptions),
  ...options,
});

export function useClientApiV1QuestionsMeQuery(params: ClientApiV1QuestionsMeQueryParams) {
  const result = useQuery<ClientApiV1QuestionsMeResponse, unknown>(
    getClientApiV1QuestionsMeQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1QuestionsBookmarks = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1QuestionsBookmarksResponse>(url, requestOptions);

export interface ClientApiV1QuestionsBookmarksQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1QuestionsBookmarksResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1QuestionsBookmarksQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1QuestionsBookmarksQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1QuestionsBookmarks(url, requestOptions),
  ...options,
});

export function useClientApiV1QuestionsBookmarksQuery(
  params: ClientApiV1QuestionsBookmarksQueryParams,
) {
  const result = useQuery<ClientApiV1QuestionsBookmarksResponse, unknown>(
    getClientApiV1QuestionsBookmarksQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1StudiesMe = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1StudiesMeResponse>(url, requestOptions);

export interface ClientApiV1StudiesMeQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1StudiesMeResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1StudiesMeQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1StudiesMeQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1StudiesMe(url, requestOptions),
  ...options,
});

export function useClientApiV1StudiesMeQuery(params: ClientApiV1StudiesMeQueryParams) {
  const result = useQuery<ClientApiV1StudiesMeResponse, unknown>(
    getClientApiV1StudiesMeQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ChatsMe = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1ChatsMeResponse>(url, requestOptions);

export interface ClientApiV1ChatsMeQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1ChatsMeResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ChatsMeQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ChatsMeQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ChatsMe(url, requestOptions),
  ...options,
});

export function useClientApiV1ChatsMeQuery(params: ClientApiV1ChatsMeQueryParams) {
  const result = useQuery<ClientApiV1ChatsMeResponse, unknown>(
    getClientApiV1ChatsMeQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1BlogsUserUserId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1BlogsUserUserIdResponse>(url, requestOptions);

export interface ClientApiV1BlogsUserUserIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1BlogsUserUserIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1BlogsUserUserIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1BlogsUserUserIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1BlogsUserUserId(url, requestOptions),
  ...options,
});

export function useClientApiV1BlogsUserUserIdQuery(params: ClientApiV1BlogsUserUserIdQueryParams) {
  const result = useQuery<ClientApiV1BlogsUserUserIdResponse, unknown>(
    getClientApiV1BlogsUserUserIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1BlogsUserUserIdCategory = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1BlogsUserUserIdCategoryResponse>(url, requestOptions);

export interface ClientApiV1BlogsUserUserIdCategoryQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1BlogsUserUserIdCategoryResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1BlogsUserUserIdCategoryQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1BlogsUserUserIdCategoryQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1BlogsUserUserIdCategory(url, requestOptions),
  ...options,
});

export function useClientApiV1BlogsUserUserIdCategoryQuery(
  params: ClientApiV1BlogsUserUserIdCategoryQueryParams,
) {
  const result = useQuery<ClientApiV1BlogsUserUserIdCategoryResponse, unknown>(
    getClientApiV1BlogsUserUserIdCategoryQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1BlogsUserUserIdTag = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1BlogsUserUserIdTagResponse>(url, requestOptions);

export interface ClientApiV1BlogsUserUserIdTagQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1BlogsUserUserIdTagResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1BlogsUserUserIdTagQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1BlogsUserUserIdTagQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1BlogsUserUserIdTag(url, requestOptions),
  ...options,
});

export function useClientApiV1BlogsUserUserIdTagQuery(
  params: ClientApiV1BlogsUserUserIdTagQueryParams,
) {
  const result = useQuery<ClientApiV1BlogsUserUserIdTagResponse, unknown>(
    getClientApiV1BlogsUserUserIdTagQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1CommunitiesFeeds = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CommunitiesFeedsResponse>(url, requestOptions);

export interface ClientApiV1CommunitiesFeedsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CommunitiesFeedsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CommunitiesFeedsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CommunitiesFeedsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CommunitiesFeeds(url, requestOptions),
  ...options,
});

export function useClientApiV1CommunitiesFeedsQuery(
  params: ClientApiV1CommunitiesFeedsQueryParams,
) {
  const result = useQuery<ClientApiV1CommunitiesFeedsResponse, unknown>(
    getClientApiV1CommunitiesFeedsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ProjectsMe = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1ProjectsMeResponse>(url, requestOptions);

export interface ClientApiV1ProjectsMeQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1ProjectsMeResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ProjectsMeQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ProjectsMeQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ProjectsMe(url, requestOptions),
  ...options,
});

export function useClientApiV1ProjectsMeQuery(params: ClientApiV1ProjectsMeQueryParams) {
  const result = useQuery<ClientApiV1ProjectsMeResponse, unknown>(
    getClientApiV1ProjectsMeQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ReviewsCourseUserUserIdReceived = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1ReviewsCourseUserUserIdReceivedResponse>(url, requestOptions);

export interface ClientApiV1ReviewsCourseUserUserIdReceivedQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1ReviewsCourseUserUserIdReceivedResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ReviewsCourseUserUserIdReceivedQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ReviewsCourseUserUserIdReceivedQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ReviewsCourseUserUserIdReceived(url, requestOptions),
  ...options,
});

export function useClientApiV1ReviewsCourseUserUserIdReceivedQuery(
  params: ClientApiV1ReviewsCourseUserUserIdReceivedQueryParams,
) {
  const result = useQuery<ClientApiV1ReviewsCourseUserUserIdReceivedResponse, unknown>(
    getClientApiV1ReviewsCourseUserUserIdReceivedQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ReviewsCoursesCourseIdBestReviews = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1ReviewsCoursesCourseIdBestReviewsResponse>(url, requestOptions);

export interface ClientApiV1ReviewsCoursesCourseIdBestReviewsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1ReviewsCoursesCourseIdBestReviewsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ReviewsCoursesCourseIdBestReviewsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ReviewsCoursesCourseIdBestReviewsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ReviewsCoursesCourseIdBestReviews(url, requestOptions),
  ...options,
});

export function useClientApiV1ReviewsCoursesCourseIdBestReviewsQuery(
  params: ClientApiV1ReviewsCoursesCourseIdBestReviewsQueryParams,
) {
  const result = useQuery<ClientApiV1ReviewsCoursesCourseIdBestReviewsResponse, unknown>(
    getClientApiV1ReviewsCoursesCourseIdBestReviewsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ReviewsIdContent = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1ReviewsIdContentResponse>(url, requestOptions);

export interface ClientApiV1ReviewsIdContentQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1ReviewsIdContentResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ReviewsIdContentQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ReviewsIdContentQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ReviewsIdContent(url, requestOptions),
  ...options,
});

export function useClientApiV1ReviewsIdContentQuery(
  params: ClientApiV1ReviewsIdContentQueryParams,
) {
  const result = useQuery<ClientApiV1ReviewsIdContentResponse, unknown>(
    getClientApiV1ReviewsIdContentQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1InstructorsIdProfile = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1InstructorsIdProfileResponse>(url, requestOptions);

export interface ClientApiV1InstructorsIdProfileQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1InstructorsIdProfileResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1InstructorsIdProfileQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1InstructorsIdProfileQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1InstructorsIdProfile(url, requestOptions),
  ...options,
});

export function useClientApiV1InstructorsIdProfileQuery(
  params: ClientApiV1InstructorsIdProfileQueryParams,
) {
  const result = useQuery<ClientApiV1InstructorsIdProfileResponse, unknown>(
    getClientApiV1InstructorsIdProfileQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1InstructorsUserIdClips = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1InstructorsUserIdClipsResponse>(url, requestOptions);

export interface ClientApiV1InstructorsUserIdClipsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1InstructorsUserIdClipsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1InstructorsUserIdClipsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1InstructorsUserIdClipsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1InstructorsUserIdClips(url, requestOptions),
  ...options,
});

export function useClientApiV1InstructorsUserIdClipsQuery(
  params: ClientApiV1InstructorsUserIdClipsQueryParams,
) {
  const result = useQuery<ClientApiV1InstructorsUserIdClipsResponse, unknown>(
    getClientApiV1InstructorsUserIdClipsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1Faqs = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1FaqsResponse>(url, requestOptions);

export interface ClientApiV1FaqsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1FaqsResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1FaqsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1FaqsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1Faqs(url, requestOptions),
  ...options,
});

export function useClientApiV1FaqsQuery(params: ClientApiV1FaqsQueryParams) {
  const result = useQuery<ClientApiV1FaqsResponse, unknown>(getClientApiV1FaqsQueryOptions(params));

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UserDevices = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1UserDevicesResponse>(url, requestOptions);

export interface ClientApiV1UserDevicesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1UserDevicesResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UserDevicesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UserDevicesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UserDevices(url, requestOptions),
  ...options,
});

export function useClientApiV1UserDevicesQuery(params: ClientApiV1UserDevicesQueryParams) {
  const result = useQuery<ClientApiV1UserDevicesResponse, unknown>(
    getClientApiV1UserDevicesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ClipsUsersHistories = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1ClipsUsersHistoriesResponse>(url, requestOptions);

export interface ClientApiV1ClipsUsersHistoriesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1ClipsUsersHistoriesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ClipsUsersHistoriesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ClipsUsersHistoriesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ClipsUsersHistories(url, requestOptions),
  ...options,
});

export function useClientApiV1ClipsUsersHistoriesQuery(
  params: ClientApiV1ClipsUsersHistoriesQueryParams,
) {
  const result = useQuery<ClientApiV1ClipsUsersHistoriesResponse, unknown>(
    getClientApiV1ClipsUsersHistoriesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1HalloweenChallengesDrawStatus = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1HalloweenChallengesDrawStatusResponse>(url, requestOptions);

export interface ClientApiV1HalloweenChallengesDrawStatusQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1HalloweenChallengesDrawStatusResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1HalloweenChallengesDrawStatusQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1HalloweenChallengesDrawStatusQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1HalloweenChallengesDrawStatus(url, requestOptions),
  ...options,
});

export function useClientApiV1HalloweenChallengesDrawStatusQuery(
  params: ClientApiV1HalloweenChallengesDrawStatusQueryParams,
) {
  const result = useQuery<ClientApiV1HalloweenChallengesDrawStatusResponse, unknown>(
    getClientApiV1HalloweenChallengesDrawStatusQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1RandomPointsDrawStatus = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1RandomPointsDrawStatusResponse>(url, requestOptions);

export interface ClientApiV1RandomPointsDrawStatusQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1RandomPointsDrawStatusResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1RandomPointsDrawStatusQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1RandomPointsDrawStatusQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1RandomPointsDrawStatus(url, requestOptions),
  ...options,
});

export function useClientApiV1RandomPointsDrawStatusQuery(
  params: ClientApiV1RandomPointsDrawStatusQueryParams,
) {
  const result = useQuery<ClientApiV1RandomPointsDrawStatusResponse, unknown>(
    getClientApiV1RandomPointsDrawStatusQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UserOnboardingsAnswers = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UserOnboardingsAnswersResponse>(url, requestOptions);

export interface ClientApiV1UserOnboardingsAnswersQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UserOnboardingsAnswersResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UserOnboardingsAnswersQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UserOnboardingsAnswersQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UserOnboardingsAnswers(url, requestOptions),
  ...options,
});

export function useClientApiV1UserOnboardingsAnswersQuery(
  params: ClientApiV1UserOnboardingsAnswersQueryParams,
) {
  const result = useQuery<ClientApiV1UserOnboardingsAnswersResponse, unknown>(
    getClientApiV1UserOnboardingsAnswersQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/**
 * created by ResponseSchemaGenerator
 */

export interface ClientApiV1CourseCategory {
  data: CourseCategoryResponse[];
}
export interface CourseCategoryResponse {
  id: number;
  slug: string;
  title: string;
  icon: string;
  children: CourseCategoryChildrenDto[];
}
export interface CourseCategoryChildrenDto {
  id: number;
  slug: string;
  title: string;
  skills: CourseCategorySkillDto[];
}
export interface CourseCategorySkillDto {
  slug: string;
  title: string;
}
export interface ClientApiV1UsersUserIdIntroduce {
  data: Maybe<FindUserProfileIntroduceResponse>;
}
export interface FindUserProfileIntroduceResponse {
  introduce: string;
}
export interface ClientApiV1UsersUserIdIntroduceUpdate {
  data: Maybe<UpsertUserProfileIntroduceResponse>;
}
export interface UpsertUserProfileIntroduceResponse {
  introduce: string;
}
export interface ClientApiV1UsersId {
  data: Maybe<FindIdBySlugResponse>;
}
export interface FindIdBySlugResponse {
  id: number;
}
export interface ClientApiV1UsersUserIdIsFollowing {
  data: Maybe<FindIsFollowingResponse>;
}
export interface FindIsFollowingResponse {
  isFollowing: boolean;
}
export interface ClientApiV1UsersIdSlug {
  data: Maybe<FindSlugResponse>;
}
export interface FindSlugResponse {
  slug?: string;
}
export interface ClientApiV1UsersUserIdProfile {
  data: Maybe<UserProfileResponse>;
}
export interface UserProfileResponse {
  id: number;
  name: string;
  slug: string;
  thumbnailUrl: string;
  introduce: string;
  isInstructor: boolean;
  isInactive: boolean;
  isDeleted: boolean;
  isRestricted: boolean;
  review: UserProfileReviewDto;
  hasPosts: boolean;
  hasBlogs: boolean;
  hasRoadmaps: boolean;
  hasClips: boolean;
  /**
   * 경력 인증 여부
   */
  isCareerVerified: boolean;
  /**
   * 경력 인증 수동 조치 (GRANT: 강제 부여, REVOKE: 강제 회수). null이면 수동 조치 없음
   */
  careerVerifiedManualAction: ('GRANT' | 'REVOKE') | null;
  /**
   * 인프런보장 뱃지 여부
   */
  isInflearnGuaranteed: boolean;
}
export interface UserProfileReviewDto {
  average: string;
  count: number;
}
export type CourseType = 'ONLINE' | 'OFFLINE' | 'CHALLENGE';
export type CourseLevelType = 'BEGINNER' | 'BASIC' | 'ADVANCED';
export type OfflineCourseType = 'CONFERENCE' | 'CLASS';
export type CourseCouponType = 'WON' | 'PERCENT';
export interface ClientApiV1CourseUserUserId {
  data: PageResponse & {
    items?: CourseUserProfileResponse[];
  };
}
export interface PageResponse {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
}
export interface CourseUserProfileResponse {
  id: number;
  type: CourseType;
  course: CourseUserProfileDto;
  offlineCourse?: CourseUserProfileOfflineDto;
  instructor: CourseUserProfileInstructorDto;
  payment: CourseUserProfilePaymentDto;
  coupon?: CourseUserProfileCouponDto;
  isHidePrice: boolean;
  hasMyReaction: boolean;
  isMentoringCodeCourse: boolean;
  voucher?: CourseUserProfileVoucherDto;
}
export interface CourseUserProfileDto {
  id: number;
  slug: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  reviewCount: number;
  studentCount: number;
  star: number;
  isExclusive: boolean;
  isNew: boolean;
  isUpdated: boolean;
  metadata: CourseUserProfileMetadataDto;
}
export interface CourseUserProfileMetadataDto {
  level: CourseLevelType;
  skills: string[];
}
export interface CourseUserProfileOfflineDto {
  type: OfflineCourseType;
  eventStartedAt: string;
  eventStartedAtUTC: string;
  eventEndedAt: string;
  eventEndedAtUTC: string;
  applyStartedAt: string;
  applyStartedAtUTC: string;
  applyEndedAt: string;
  applyEndedAtUTC: string;
  capacity: number;
  applicantCount: number;
  canAnswerSurvey: boolean;
  hasSurveyAnswer: boolean;
}
export interface CourseUserProfileInstructorDto {
  id: number;
  name: string;
}
export interface CourseUserProfilePaymentDto {
  hasDiscount: boolean;
  payPrice: number;
  regularPrice: number;
  isFree: boolean;
  discountRate: number;
  discountEndedAt: string;
}
export interface CourseUserProfileCouponDto {
  id: number;
  type: CourseCouponType;
  value: number;
}
export interface CourseUserProfileVoucherDto {
  completeCount: number;
  lectureCount: number;
}
export interface ClientApiV1CoursesCourseIdLikeUpdate {
  data: Maybe<string>;
}
export interface ClientApiV2CoursesIdMyVouchers {
  data: Maybe<CourseOneWithVoucherResponseV2>;
}
export interface CourseOneWithVoucherResponseV2 {
  id: number;
  title: string;
  lectureCount: number;
  missionCount: number;
  runtime: number;
  voucher: CourseVoucherResponse;
  hasVoucher: boolean;
  isBegin: boolean;
  hasFaq: boolean;
  mission: CourseMissionResponse;
}
export interface CourseVoucherResponse {
  id: number;
  limitedAt: string;
  limitedAtUtc: string;
  limitedAtUTC: string;
  completeCount: number;
  completeRuntime: number;
  progress: string;
  isDownloaded: boolean;
  isVideoDownloaded: boolean;
  isUnlimited: boolean;
  isReviewable: boolean;
  hasNote: boolean;
  noteId?: number;
  now: string;
  nowUTC: string;
  isCompleted: boolean;
  lastUnitId?: number;
}
export interface CourseMissionResponse {
  /**
   * 완료한 미션 개수
   */
  completeCount: number;
}
export type CommunityPostResponseType = 'question' | 'ANSWER' | 'chat' | 'study' | 'PROJECT';
export type PostStatus = 'resolved' | 'unresolved' | 'unrecruited' | 'recruited';
export interface ClientApiV1PostsUserUserId {
  data: PageResponse & {
    items?: PostByUserResponse[];
  };
}
export interface PostByUserResponse {
  id: number;
  commentId?: number;
  type: CommunityPostResponseType;
  status: PostStatus;
  title: string;
  body: string;
  recommendedCount: number;
  commentCount: number;
  viewCount: number;
  createdAt: string;
  createdAtUTC: string;
  slug: string;
}
export interface ClientApiV1BookmarksQuestionIdUpdate {
  data: Maybe<never>;
}
export interface ClientApiV1FilesPostImageCreate {
  data: Maybe<UrlOneResponse>;
}
export interface UrlOneResponse {
  url: string;
}
export interface ClientApiV1NotesId {
  data: Maybe<NoteOneResponse>;
}
export interface NoteOneResponse {
  id: number;
  body: string;
  unitCurrentTime: number;
}
export interface ClientApiV1NotesIdUpdate {
  data: Maybe<String>;
}
export interface String {
  [key: string]: never;
}

export interface ClientApiV1NotesIdDelete {
  data: Maybe<String>;
}
export interface ClientApiV1NotesPublishCreate {
  data: Maybe<string>;
}
export interface ClientApiV1NotesUnpublishCreate {
  data: Maybe<string>;
}
export interface ClientApiV1NotesMe {
  data: NoteByUserV2Response[];
}
export interface NoteByUserV2Response {
  id: number;
  count: number;
  isPublic: boolean;
  course: NoteByUserCourseDto;
}
export interface NoteByUserCourseDto {
  id: number;
  title: string;
  coverFile: string;
  lastStudiedAt: string;
  lastStudiedAtUTC: string;
}
export interface ClientApiV1NotesMyRecents {
  data: Maybe<MyRecentNotesResponse>;
}
export interface MyRecentNotesResponse {
  items: MyRecentNotesItemResponse[];
}
export interface MyRecentNotesItemResponse {
  id: number;
  courseId: number;
  courseTitle: string;
  createdAt: string;
  createdAtUTC: string;
}
export interface ClientApiV1NotesIdWithUnits {
  data: Maybe<NoteWithUnitsResponse>;
}
export interface NoteWithUnitsResponse {
  course: NoteWithUnitsCourseResponse;
  writer: NoteWithUnitsWriterResponse;
  sections: NoteWithUnitsSectionResponse[];
}
export interface NoteWithUnitsCourseResponse {
  id: number;
  title: string;
  slug: string;
  thumbnailUrl: string;
}
export interface NoteWithUnitsWriterResponse {
  id: number;
  name: string;
  thumbnailUrl: string;
}
export interface NoteWithUnitsSectionResponse {
  id: number;
  title: string;
  units: NoteWithUnitsUnitResponse[];
}
export interface NoteWithUnitsUnitResponse {
  id: number;
  title: string;
  notes: NoteWithUnitsNoteDetailResponse[];
}
export interface NoteWithUnitsNoteDetailResponse {
  id: number;
  unitCurrentTime: number;
  body: string;
}
export interface ClientApiV1NotesIdMetadata {
  data: Maybe<CourseNoteMetadataResponse>;
}
export interface CourseNoteMetadataResponse {
  isWritable: boolean;
  isPublic: boolean;
}
export interface ClientApiV1GuidesId {
  data: Maybe<GetGuideResponse>;
}
export interface GetGuideResponse {
  title: string;
  body: string;
}
export interface ClientApiV1GuidesIdDelete {
  data: Maybe<string>;
}
export type QuestionStatus = 'RESOLVED' | 'UNRESOLVED';
export interface ClientApiV1QuestionsMe {
  data: QuestionByUserResponse[];
}
export interface QuestionByUserResponse {
  id: number;
  title: string;
  slug: string;
  body: string;
  courseTitle?: string;
  tags: string[];
  isRecommended: boolean;
  recommendCount: number;
  viewCount: number;
  commentCount: number;
  status: QuestionStatus;
  createdAt: string;
  createdAtUTC: string;
}
export interface ClientApiV1QuestionsBookmarks {
  data: BookmarkedQuestionResponse[];
}
export interface BookmarkedQuestionResponse {
  id: number;
  writerName: string;
  title: string;
  slug?: string;
  body: string;
  courseTitle?: string;
  tags: string[];
  isRecommended: boolean;
  recommendCount: number;
  bookmarkCount: number;
  commentCount: number;
  status: QuestionStatus;
  createdAt: string;
  createdAtUTC: string;
}
export type StudyStatus = 'RECRUITING' | 'RECRUITED';
export interface ClientApiV1StudiesMe {
  data: StudyByUserResponse[];
}
export interface StudyByUserResponse {
  id: number;
  title: string;
  body: string;
  courseTitle?: string;
  tags: string[];
  isLiked: boolean;
  likeCount: number;
  viewCount: number;
  commentCount: number;
  status: StudyStatus;
  createdAt: string;
  createdAtUTC: string;
}
export interface ClientApiV1ChatsMe {
  data: ChatByUserResponse[];
}
export interface ChatByUserResponse {
  id: number;
  title: string;
  body: string;
  courseTitle?: string;
  tags: string[];
  isLiked: boolean;
  likeCount: number;
  viewCount: number;
  commentCount: number;
  createdAt: string;
  createdAtUTC: string;
}
export type BlogStatusType = 'DRAFT' | 'PUBLISH';
export interface ClientApiV1BlogsUserUserId {
  data: PageResponse & {
    items?: BlogUserProfileResponse[];
  };
}
export interface BlogUserProfileResponse {
  id: number;
  title: string;
  body: string;
  status: BlogStatusType;
  likeCount: number;
  createdAt: string;
  createdAtUTC: string;
  coverUrl: string;
  writer: BlogUserProfileUserDto;
  category?: BlogUserProfileCategoryDto;
  tags: BlogUserProfileTagDto[];
}
export interface BlogUserProfileUserDto {
  id: number;
  name: string;
  isAdmin: boolean;
}
export interface BlogUserProfileCategoryDto {
  id: number;
  name: string;
}
export interface BlogUserProfileTagDto {
  id: number;
  name: string;
}
export interface ClientApiV1BlogsUserUserIdCategory {
  data: Maybe<BlogUserProfileCategoryResponse>;
}
export interface BlogUserProfileCategoryResponse {
  names: string[];
}
export interface ClientApiV1BlogsUserUserIdTag {
  data: Maybe<BlogUserProfileTagResponse>;
}
export interface BlogUserProfileTagResponse {
  names: string[];
}
export interface ClientApiV1CommunitiesFeeds {
  data: Maybe<CommunityFeedsResponse>;
}
export interface CommunityFeedsResponse {
  /**
   * 커뮤니티 피드 항목 목록
   */
  items: CommunityFeedItemResponse[];
}
export interface CommunityFeedItemResponse {
  /**
   * 커뮤니티 콘텐츠 타입
   */
  type:
    | 'STUDY'
    | 'QUESTION'
    | 'QUESTION_COMMENT'
    | 'PROJECT'
    | 'CHAT'
    | 'BLOG'
    | 'COURSE_REVIEW'
    | 'ROADMAP'
    | 'MENTEE_REVIEW';
  /**
   * 작성자 정보 (작성자가 없는 경우 생략)
   */
  user?: FeedUserResponse;
  /**
   * 작성 시간 또는 게시 시간 (UTC ISO 8601)
   */
  createdAtUTC: string;
  /**
   * 질문 정보 (type=QUESTION 일 때만 존재)
   */
  question?: FeedQuestionResponse;
  /**
   * 답변 정보 (type=QUESTION_COMMENT 일 때만 존재)
   */
  questionComment?: FeedQuestionCommentResponse;
  /**
   * 강의 리뷰 정보 (type=COURSE_REVIEW 일 때만 존재)
   */
  courseReview?: FeedCourseReviewResponse;
  /**
   * 멘티리뷰 정보 (type=MENTEE_REVIEW 일 때만 존재)
   */
  menteeReview?: FeedMenteeReviewResponse;
  /**
   * 블로그 정보 (type=BLOG 일 때만 존재)
   */
  blog?: FeedBlogResponse;
  /**
   * 스터디 정보 (type=STUDY 일 때만 존재)
   */
  study?: FeedStudyResponse;
  /**
   * 프로젝트 정보 (type=PROJECT 일 때만 존재)
   */
  project?: FeedProjectResponse;
  /**
   * 채팅 정보 (type=CHAT 일 때만 존재)
   */
  chat?: FeedChatResponse;
}
export interface FeedUserResponse {
  /**
   * 작성자 이름
   */
  name: string;
  /**
   * 작성자 썸네일 URL
   */
  thumbnailUrl: string;
}
export interface FeedQuestionResponse {
  /**
   * 질문 ID
   */
  id: number;
  /**
   * 질문 제목
   */
  title: string;
  /**
   * 질문 슬러그
   */
  slug?: string;
  /**
   * 강의 정보 (강의에 속한 질문일 때만 존재)
   */
  course?: FeedCourseResponse;
}
export interface FeedCourseResponse {
  /**
   * 강의 ID
   */
  id: number;
  /**
   * 강의 제목 (요청 언어 번역본, 없으면 원문)
   */
  title: string;
  /**
   * 대표지식공유자 이름
   */
  instructorName: string;
}
export interface FeedQuestionCommentResponse {
  /**
   * 답변 ID
   */
  id: number;
  /**
   * 질문 ID
   */
  questionId: number;
  /**
   * 질문 슬러그
   */
  questionSlug?: string;
  /**
   * 질문 제목
   */
  questionTitle: string;
  /**
   * 답변 요약 본문
   */
  textSummaryBody: string;
  /**
   * 강의 정보 (강의에 속한 질문답변일 때만 존재)
   */
  course?: FeedCourseResponse;
}
export interface FeedCourseReviewResponse {
  /**
   * 강의 리뷰 ID
   */
  id: number;
  /**
   * 리뷰 본문
   */
  content: string;
  /**
   * 별점
   */
  rating: number;
  /**
   * 강의 정보
   */
  course: FeedCourseResponse;
}
export interface FeedMenteeReviewResponse {
  /**
   * 멘티리뷰 ID (mentee-review post id)
   */
  id: number;
  /**
   * 멘토링 제목
   */
  mentoringTitle: string;
  /**
   * 멘티리뷰 요약 본문
   */
  textSummaryBody: string;
  /**
   * 별점
   */
  star: number;
}
export interface FeedBlogResponse {
  /**
   * 블로그 ID
   */
  id: number;
  /**
   * 블로그 제목
   */
  title: string;
  /**
   * 블로그 요약 본문
   */
  textSummaryBody: string;
}
export interface FeedStudyResponse {
  /**
   * 스터디 ID
   */
  id: number;
  /**
   * 스터디 제목
   */
  title: string;
  /**
   * 스터디 슬러그
   */
  slug?: string;
  /**
   * 스터디 요약 본문
   */
  textSummaryBody: string;
}
export interface FeedProjectResponse {
  /**
   * 프로젝트 ID
   */
  id: number;
  /**
   * 프로젝트 제목
   */
  title: string;
  /**
   * 프로젝트 슬러그
   */
  slug?: string;
  /**
   * 프로젝트 요약 본문
   */
  textSummaryBody: string;
}
export interface FeedChatResponse {
  /**
   * 채팅 ID
   */
  id: number;
  /**
   * 채팅 제목
   */
  title: string;
  /**
   * 채팅 슬러그
   */
  slug?: string;
  /**
   * 채팅 요약 본문
   */
  textSummaryBody: string;
}
export type ProjectStatus = 'RECRUITING' | 'RECRUITED';
export interface ClientApiV1ProjectsMe {
  data: ProjectByUserResponse[];
}
export interface ProjectByUserResponse {
  id: number;
  title: string;
  body: string;
  tags: string[];
  isLiked: boolean;
  likeCount: number;
  viewCount: number;
  commentCount: number;
  status: ProjectStatus;
  createdAt: string;
  createdAtUTC: string;
}
export interface ClientApiV1ReviewsCourseUserUserIdReceived {
  data: PageResponse & {
    items?: ReviewUserProfileResponse[];
  };
}
export interface ReviewUserProfileResponse {
  id: number;
  body: string;
  score: number;
  likeCount: number;
  createdAt: string;
  createdAtUTC: string;
  writer: ReviewUserProfileUserDto;
  course: ReviewUserProfileCourseDto;
  originalLanguage: 'ko' | 'en' | 'ja' | 'vi';
  contentLanguage: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ReviewUserProfileUserDto {
  id: number;
  name: string;
}
export interface ReviewUserProfileCourseDto {
  id: number;
  title: string;
  slug: string;
  thumbnailUrl: string;
  courseUrl: string;
}
export interface ClientApiV1ReviewsCoursesCourseIdBestReviews {
  data: Maybe<BestReviewResponse>;
}
export interface BestReviewResponse {
  bestReviews: BestReviewDto[];
}
export interface BestReviewDto {
  id: number;
  reviewContent: string;
  unescapedReviewContent: string;
  reviewRating: number;
  createdAt: string;
  createdAtUTC: string;
  courseProgress: number;
  originalLanguage: 'ko' | 'en' | 'ja' | 'vi';
  contentLanguage: 'ko' | 'en' | 'ja' | 'vi';
  author: BestReviewAuthorDto;
  isPinned: boolean;
  isExperienceGroup: boolean;
}
export interface BestReviewAuthorDto {
  id: number;
  name: string;
  profileUrl: string;
  totalReviewCount: number;
  averageReviewRating: number;
}
export interface ClientApiV1ReviewsIdContent {
  data: Maybe<ReviewOneContentResponse>;
}
export interface ReviewOneContentResponse {
  content: string;
  originalLanguage: 'ko' | 'en' | 'ja' | 'vi';
  contentLanguage: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1FollowsUserIdUpdate {
  data: Maybe<string>;
}
export interface ClientApiV1InstructorsIdProfile {
  data: Maybe<InstructorProfileResponse>;
}
export interface InstructorProfileResponse {
  user?: UserDto;
  mentor?: MentorDto;
  reviewAverage: string;
  reviewCount: number;
  studentCount: number;
  courseCount: number;
  hasRoadmap: boolean;
}
export interface UserDto {
  id: number;
  name: string;
  thumbnail: string;
  hasRoadmap: boolean;
}
export interface MentorDto {
  id: number;
  introduction?: string;
  jobGroup: string;
  job: string;
  jobLevel?: JobLevelDto;
  companyName: string;
  isActive: boolean;
  mentoringCount: number;
  reviewAverage: string;
  reviewCount: number;
}
export interface JobLevelDto {
  code: string;
  name: string;
}
export interface ClientApiV1InstructorsUserIdClips {
  data: PageResponse & {
    items?: InstructorClipResponse[];
  };
}
export interface InstructorClipResponse {
  id: number;
  title: string;
  thumbnailUrl: string;
  author: InstructorClipAuthor;
  attachment?: InstructorClipAttachment;
  textBody: string;
  createdAtUTC: string;
  updatedAtUTC: string;
  publishedAtUTC?: string;
  lastUpdatedAtUTC?: string;
  viewCount: number;
  clapCount: number;
  commentCount: number;
  commonTags: string[];
}
export interface InstructorClipAuthor {
  id: number;
  thumbnailUrl: string;
  name: string;
  slug: string;
}
export interface InstructorClipAttachment {
  name: string;
}
export interface ClientApiV1Faqs {
  data: FaqOneResponse[];
}
export interface FaqOneResponse {
  title: string;
  body: string;
  type: 'LOGIN' | 'ERROR' | 'USAGE' | 'VIDEO_DOWNLOAD' | 'ETC';
}
export interface ClientApiV1UserDevices {
  data: Maybe<UserDeviceFindResponse>;
}
export interface UserDeviceFindResponse {
  devices: UserDeviceOneResponse[];
}
export interface UserDeviceOneResponse {
  id: number;
  userAgent: string;
  osName: string;
  clientName: string;
  lastAccessedAt: string;
  lastAccessedAtUTC: string;
  isRequesterDevice: boolean;
}
export interface ClientApiV1UserDevicesRegisterCreate {
  data: Maybe<UserDeviceRegisterResponse>;
}
export interface UserDeviceRegisterResponse {
  /**
   * 사용자 기기 토큰
   */
  token: string;
}
export interface ClientApiV1UserDevicesSendVerificationEmailCreate {
  data: Maybe<string>;
}
export interface ClientApiV1UserDevicesEmailVerificationCreate {
  data: Maybe<UserDeviceEmailVerificationResponse>;
}
export interface UserDeviceEmailVerificationResponse {
  /**
   * 기기 삭제를 위한 토큰
   */
  token: string;
}
export interface ClientApiV1UserDevicesConfirmTokenCreate {
  data: Maybe<string>;
}
export interface ClientApiV1UserDevicesIdRemoveCreate {
  data: Maybe<string>;
}
export interface ClientApiV1ClipsUsersHistories {
  data: PageResponse & {
    items?: ClipUserHistoryResponse[];
  };
}
export interface ClipUserHistoryResponse {
  id: number;
  title: string;
  thumbnailUrl: string;
  /**
   * 최대 150자
   */
  textBody: string;
  author: ClipUserHistoryAuthorResponse;
  voucher?: ClipUserHistoryVoucherResponse;
  attachment?: ClipUserHistoryAttachmentResponse;
  createdAtUTC: string;
  updatedAtUTC: string;
  publishedAtUTC?: string;
  lastUpdatedAtUTC?: string;
  viewCount: number;
  clapCount: number;
  commentCount: number;
  commonTags: string[];
}
export interface ClipUserHistoryAuthorResponse {
  id: number;
  thumbnailUrl: string;
  name: string;
  slug: string;
}
export interface ClipUserHistoryVoucherResponse {
  id: number;
  isValid: boolean;
  limitedAtUTC?: string;
}
export interface ClipUserHistoryAttachmentResponse {
  name: string;
}
export interface ClientApiV1ClipsCreate {
  data: Maybe<CreateClipResponse>;
}
export interface CreateClipResponse {
  id: number;
}
export interface ClientApiV1HalloweenChallengesDrawStatus {
  data: Maybe<HalloweenChallengeDrawStatusResponse>;
}
export interface HalloweenChallengeDrawStatusResponse {
  point?: number;
  status: 'RECEIVED' | 'NOT_RECEIVE';
}
export interface ClientApiV1HalloweenChallengesDrawPointsCreate {
  data: Maybe<HalloweenChallengeDrawPointResponse>;
}
export interface HalloweenChallengeDrawPointResponse {
  point: number;
}
export interface ClientApiV1RandomPointsDrawStatus {
  data: Maybe<RandomPointDrawStatusResponse>;
}
export interface RandomPointDrawStatusResponse {
  point: number | null;
  status: 'RECEIVED' | 'NOT_RECEIVED';
}
export interface ClientApiV1RandomPointsDrawCourseCreate {
  data: Maybe<CourseRandomPointDrawResponse>;
}
export interface CourseRandomPointDrawResponse {
  /**
   * 지급 포인트
   */
  point: number;
}
export interface ClientApiV1RandomPointsDrawCreate {
  data: Maybe<RandomPointDrawResponse>;
}
export interface RandomPointDrawResponse {
  /**
   * 지급 포인트
   */
  point: number;
}
export type OnboardingAnswerType =
  | 'CUSTOM_INPUT'
  | 'USAGE_PURPOSE'
  | 'UNKNOWN'
  | 'CATEGORY'
  | 'SKILL_TAG';
export interface ClientApiV1UserOnboardingsAnswers {
  data: Maybe<GetUserOnboardingAnswersResponse>;
}
export interface GetUserOnboardingAnswersResponse {
  usagePurposes: OnboardingAnswerResponse[];
  categories: OnboardingAnswerResponse[];
  skillTags: OnboardingAnswerResponse[];
}
export interface OnboardingAnswerResponse {
  id: number;
  content?: string;
  type: OnboardingAnswerType;
  description?: string;
  slug?: string;
  usagePurposeId?: number;
  categoryId?: number;
  skillId?: number;
}
export interface ClientApiV1UserOnboardingsAnswersCreate {
  data: Maybe<string>;
}
export type ClientApiV1CourseCategoryResponse = ApiResponse<ClientApiV1CourseCategory['data']>;
export type ClientApiV1UsersUserIdIntroduceResponse = ApiResponse<
  ClientApiV1UsersUserIdIntroduce['data']
>;
export type ClientApiV1UsersUserIdIntroduceUpdateResponse = ApiResponse<
  ClientApiV1UsersUserIdIntroduceUpdate['data']
>;
export type ClientApiV1UsersIdResponse = ApiResponse<ClientApiV1UsersId['data']>;
export type ClientApiV1UsersUserIdIsFollowingResponse = ApiResponse<
  ClientApiV1UsersUserIdIsFollowing['data']
>;
export type ClientApiV1UsersIdSlugResponse = ApiResponse<ClientApiV1UsersIdSlug['data']>;
export type ClientApiV1UsersUserIdProfileResponse = ApiResponse<
  ClientApiV1UsersUserIdProfile['data']
>;
export type ClientApiV1CourseUserUserIdResponse = ApiResponse<ClientApiV1CourseUserUserId['data']>;
export type ClientApiV1CoursesCourseIdLikeUpdateResponse = ApiResponse<
  ClientApiV1CoursesCourseIdLikeUpdate['data']
>;
export type ClientApiV2CoursesIdMyVouchersResponse = ApiResponse<
  ClientApiV2CoursesIdMyVouchers['data']
>;
export type ClientApiV1PostsUserUserIdResponse = ApiResponse<ClientApiV1PostsUserUserId['data']>;
export type ClientApiV1BookmarksQuestionIdUpdateResponse = ApiResponse<
  ClientApiV1BookmarksQuestionIdUpdate['data']
>;
export type ClientApiV1FilesPostImageCreateResponse = ApiResponse<
  ClientApiV1FilesPostImageCreate['data']
>;
export type ClientApiV1NotesIdResponse = ApiResponse<ClientApiV1NotesId['data']>;
export type ClientApiV1NotesIdUpdateResponse = ApiResponse<ClientApiV1NotesIdUpdate['data']>;
export type ClientApiV1NotesIdDeleteResponse = ApiResponse<ClientApiV1NotesIdDelete['data']>;
export type ClientApiV1NotesPublishCreateResponse = ApiResponse<
  ClientApiV1NotesPublishCreate['data']
>;
export type ClientApiV1NotesUnpublishCreateResponse = ApiResponse<
  ClientApiV1NotesUnpublishCreate['data']
>;
export type ClientApiV1NotesMeResponse = ApiResponse<ClientApiV1NotesMe['data']>;
export type ClientApiV1NotesMyRecentsResponse = ApiResponse<ClientApiV1NotesMyRecents['data']>;
export type ClientApiV1NotesIdWithUnitsResponse = ApiResponse<ClientApiV1NotesIdWithUnits['data']>;
export type ClientApiV1NotesIdMetadataResponse = ApiResponse<ClientApiV1NotesIdMetadata['data']>;
export type ClientApiV1GuidesIdResponse = ApiResponse<ClientApiV1GuidesId['data']>;
export type ClientApiV1GuidesIdDeleteResponse = ApiResponse<ClientApiV1GuidesIdDelete['data']>;
export type ClientApiV1QuestionsMeResponse = ApiResponse<ClientApiV1QuestionsMe['data']>;
export type ClientApiV1QuestionsBookmarksResponse = ApiResponse<
  ClientApiV1QuestionsBookmarks['data']
>;
export type ClientApiV1StudiesMeResponse = ApiResponse<ClientApiV1StudiesMe['data']>;
export type ClientApiV1ChatsMeResponse = ApiResponse<ClientApiV1ChatsMe['data']>;
export type ClientApiV1BlogsUserUserIdResponse = ApiResponse<ClientApiV1BlogsUserUserId['data']>;
export type ClientApiV1BlogsUserUserIdCategoryResponse = ApiResponse<
  ClientApiV1BlogsUserUserIdCategory['data']
>;
export type ClientApiV1BlogsUserUserIdTagResponse = ApiResponse<
  ClientApiV1BlogsUserUserIdTag['data']
>;
export type ClientApiV1CommunitiesFeedsResponse = ApiResponse<ClientApiV1CommunitiesFeeds['data']>;
export type ClientApiV1ProjectsMeResponse = ApiResponse<ClientApiV1ProjectsMe['data']>;
export type ClientApiV1ReviewsCourseUserUserIdReceivedResponse = ApiResponse<
  ClientApiV1ReviewsCourseUserUserIdReceived['data']
>;
export type ClientApiV1ReviewsCoursesCourseIdBestReviewsResponse = ApiResponse<
  ClientApiV1ReviewsCoursesCourseIdBestReviews['data']
>;
export type ClientApiV1ReviewsIdContentResponse = ApiResponse<ClientApiV1ReviewsIdContent['data']>;
export type ClientApiV1FollowsUserIdUpdateResponse = ApiResponse<
  ClientApiV1FollowsUserIdUpdate['data']
>;
export type ClientApiV1InstructorsIdProfileResponse = ApiResponse<
  ClientApiV1InstructorsIdProfile['data']
>;
export type ClientApiV1InstructorsUserIdClipsResponse = ApiResponse<
  ClientApiV1InstructorsUserIdClips['data']
>;
export type ClientApiV1FaqsResponse = ApiResponse<ClientApiV1Faqs['data']>;
export type ClientApiV1UserDevicesResponse = ApiResponse<ClientApiV1UserDevices['data']>;
export type ClientApiV1UserDevicesRegisterCreateResponse = ApiResponse<
  ClientApiV1UserDevicesRegisterCreate['data']
>;
export type ClientApiV1UserDevicesSendVerificationEmailCreateResponse = ApiResponse<
  ClientApiV1UserDevicesSendVerificationEmailCreate['data']
>;
export type ClientApiV1UserDevicesEmailVerificationCreateResponse = ApiResponse<
  ClientApiV1UserDevicesEmailVerificationCreate['data']
>;
export type ClientApiV1UserDevicesConfirmTokenCreateResponse = ApiResponse<
  ClientApiV1UserDevicesConfirmTokenCreate['data']
>;
export type ClientApiV1UserDevicesIdRemoveCreateResponse = ApiResponse<
  ClientApiV1UserDevicesIdRemoveCreate['data']
>;
export type ClientApiV1ClipsUsersHistoriesResponse = ApiResponse<
  ClientApiV1ClipsUsersHistories['data']
>;
export type ClientApiV1ClipsCreateResponse = ApiResponse<ClientApiV1ClipsCreate['data']>;
export type ClientApiV1HalloweenChallengesDrawStatusResponse = ApiResponse<
  ClientApiV1HalloweenChallengesDrawStatus['data']
>;
export type ClientApiV1HalloweenChallengesDrawPointsCreateResponse = ApiResponse<
  ClientApiV1HalloweenChallengesDrawPointsCreate['data']
>;
export type ClientApiV1RandomPointsDrawStatusResponse = ApiResponse<
  ClientApiV1RandomPointsDrawStatus['data']
>;
export type ClientApiV1RandomPointsDrawCourseCreateResponse = ApiResponse<
  ClientApiV1RandomPointsDrawCourseCreate['data']
>;
export type ClientApiV1RandomPointsDrawCreateResponse = ApiResponse<
  ClientApiV1RandomPointsDrawCreate['data']
>;
export type ClientApiV1UserOnboardingsAnswersResponse = ApiResponse<
  ClientApiV1UserOnboardingsAnswers['data']
>;
export type ClientApiV1UserOnboardingsAnswersCreateResponse = ApiResponse<
  ClientApiV1UserOnboardingsAnswersCreate['data']
>;

/**
 * created by DTOGenerator
 */

export interface UpsertUserProfileIntroduceRequest {
  body: string;
}
export interface CourseUserProfileLikeStatusRequest {
  status: 'LIKE' | 'UNLIKE';
}
export interface BookmarkV2UpdateRequest {
  isBookmarked: boolean;
}
export interface FileUploadRequest {
  file: string;
}
export interface NoteUpdateRequest {
  body: string;
}
export interface NotePublishRequest {
  id: number;
}
export interface FollowStatusChangeRequest {
  isFollowing: boolean;
}
export interface UserDeviceRegisterRequest {
  /**
   * 기기 모델명
   */
  deviceModelName?: string;
}
export interface UserDeviceEmailVerificationRequest {
  code: string;
}
export interface UserDeviceRemoveRequest {
  /**
   * 기기 삭제를 위한 토큰
   */
  token: string;
}
export interface HalloweenChallengeDrawPointRequest {
  key: string;
}
export interface CourseRandomPointDrawRequest {
  /**
   * 랜덤 포인트 키
   */
  key: string;
}
export interface RandomPointDrawRequest {
  /**
   * 랜덤 포인트 키
   */
  key: string;
}
export type OnboardingQuestionType = 'USAGE_PURPOSE' | 'CATEGORY' | 'SKILL_TAG';
export interface SaveUserOnboardingAnswersRequest {
  type: OnboardingQuestionType;
  answers: OnboardingAnswerRequest[];
}
export interface OnboardingAnswerRequest {
  type: OnboardingAnswerType;
  content?: string;
  usagePurposeId?: number;
  categoryId?: number;
  skillId?: number;
}
export interface ClientApiV1CourseCategoryQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1UsersUserIdIntroduceUpdateUrlParams {
  /**
   * 유저 id
   */
  userId: string;
}
export interface ClientApiV1UsersUserIdIntroduceUpdateVariables {
  body: string;
}
export interface ClientApiV1UsersIdQueryString {
  slug: string;
}
export interface ClientApiV1UsersUserIdProfileQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1CourseUserUserIdQueryString {
  pageNumber: number;
  pageSize: number;
  sort: 'RECENT' | 'POPULAR' | 'RECOMMEND';
}
export interface ClientApiV1CoursesCourseIdLikeUpdateUrlParams {
  /**
   * 좋아요 상태를 변경하려는 강의 id
   */
  courseId: string;
}
export interface ClientApiV1CoursesCourseIdLikeUpdateVariables {
  status: 'LIKE' | 'UNLIKE';
}
export interface ClientApiV2CoursesIdMyVouchersQueryString {
  lang: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1PostsUserUserIdQueryString {
  pageNumber: number;
  pageSize: number;
  sort: 'RECOMMEND' | 'RECENT' | 'OLDER';
  type: 'QUESTION' | 'ANSWER' | 'CHAT' | 'STUDY' | 'PROJECT' | 'ALL';
}
export interface ClientApiV1BookmarksQuestionIdUpdateUrlParams {
  /**
   * 질문 id
   */
  id: string;
}
export interface ClientApiV1BookmarksQuestionIdUpdateVariables {
  isBookmarked: boolean;
}
export interface ClientApiV1FilesPostImageCreateVariables {
  file: string;
}
export interface ClientApiV1NotesIdUpdateUrlParams {
  /**
   * 노트 id
   */
  id: string;
}
export interface ClientApiV1NotesIdUpdateVariables {
  body: string;
}
export interface ClientApiV1NotesIdDeleteUrlParams {
  /**
   * 노트 id
   */
  id: string;
}
export interface ClientApiV1NotesIdDeleteVariables {
  [k: string]: unknown;
}
export interface ClientApiV1NotesPublishCreateVariables {
  id: number;
}
export interface ClientApiV1NotesUnpublishCreateVariables {
  id: number;
}
export interface ClientApiV1NotesMeQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  searchKeyword?: string;
  searchType?: 'ALL' | 'COURSE' | 'NOTE';
  sort?: 'ENROLLED' | 'STUDIED';
}
export interface ClientApiV1NotesMyRecentsQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  userId?: number;
}
export interface ClientApiV1NotesIdWithUnitsQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1GuidesIdDeleteUrlParams {
  /**
   * 가이드 id
   */
  id: string;
}
export interface ClientApiV1GuidesIdDeleteVariables {
  [k: string]: unknown;
}
export interface ClientApiV1QuestionsMeQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  status: 'ALL' | 'RESOLVED' | 'UNRESOLVED';
}
export interface ClientApiV1QuestionsBookmarksQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  status: 'ALL' | 'RESOLVED' | 'UNRESOLVED';
}
export interface ClientApiV1StudiesMeQueryString {
  status: 'ALL' | 'RECRUITING' | 'RECRUITED';
}
export interface ClientApiV1BlogsUserUserIdQueryString {
  pageNumber: number;
  pageSize: number;
  filter?: 'CATEGORY' | 'TAG';
  filterName?: string;
}
export interface ClientApiV1CommunitiesFeedsQueryString {
  limit?: number;
  lang?: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1ProjectsMeQueryString {
  status: 'ALL' | 'RECRUITING' | 'RECRUITED';
}
export interface ClientApiV1ReviewsCourseUserUserIdReceivedQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  pageNumber: number;
  pageSize: number;
}
export interface ClientApiV1ReviewsCoursesCourseIdBestReviewsQueryString {
  lang: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1ReviewsIdContentQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi' | 'original';
}
export interface ClientApiV1FollowsUserIdUpdateUrlParams {
  /**
   * 팔로우할 유저 id
   */
  id: string;
}
export interface ClientApiV1FollowsUserIdUpdateVariables {
  isFollowing: boolean;
}
export interface ClientApiV1InstructorsIdProfileQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  excludeUser?: boolean;
}
export interface ClientApiV1InstructorsUserIdClipsQueryString {
  pageNumber: number;
  pageSize: number;
  excludeClipIds?: string;
}
export interface ClientApiV1FaqsQueryString {
  usage: 'WEB' | 'APP';
}
export interface ClientApiV1UserDevicesRegisterCreateVariables {
  /**
   * 기기 모델명
   */
  deviceModelName?: string;
}
export interface ClientApiV1UserDevicesSendVerificationEmailCreateVariables {
  [k: string]: unknown;
}
export interface ClientApiV1UserDevicesEmailVerificationCreateVariables {
  code: string;
}
export interface ClientApiV1UserDevicesConfirmTokenCreateVariables {
  [k: string]: unknown;
}
export interface ClientApiV1UserDevicesIdRemoveCreateVariables {
  /**
   * 기기 삭제를 위한 토큰
   */
  token: string;
}
export interface ClientApiV1ClipsUsersHistoriesQueryString {
  pageNumber: number;
  pageSize: number;
  lang: 'ko' | 'en' | 'ja' | 'vi';
  searchKeyword?: string;
  paid?: 'PAID' | 'FREE' | 'ALL';
  sort?: 'RECENT_STUDYING' | 'CLIP_TITLE';
  userId?: number;
}
export interface ClientApiV1ClipsCreateVariables {
  [k: string]: unknown;
}
export interface ClientApiV1HalloweenChallengesDrawStatusQueryString {
  key: string;
}
export interface ClientApiV1HalloweenChallengesDrawPointsCreateVariables {
  key: string;
}
export interface ClientApiV1RandomPointsDrawStatusQueryString {
  key: string;
}
export interface ClientApiV1RandomPointsDrawCourseCreateVariables {
  /**
   * 랜덤 포인트 키
   */
  key: string;
}
export interface ClientApiV1RandomPointsDrawCreateVariables {
  /**
   * 랜덤 포인트 키
   */
  key: string;
}
export interface ClientApiV1UserOnboardingsAnswersQueryString {
  lang: 'ko' | 'en' | 'ja' | 'vi';
}
export interface ClientApiV1UserOnboardingsAnswersCreateVariables {
  type: OnboardingQuestionType;
  answers: OnboardingAnswerRequest[];
}
